import React, { useState, useEffect } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Save, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import AssignedTrackingForms from './AssignedTrackingForms';

export default function SubscriberTracking() {
  const { user } = useAuth();
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showHistory, setShowHistory] = useState(false);

  const [formData, setFormData] = useState({
    on_time_percentage: 50,
    off_time_percentage: 50,
    dyskinesia_level: 3,
    sleep_quality: 5,
    energy_level: 5,
    mood: 5,
    notes: ''
  });

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;

  useEffect(() => {
    const fetchCheckins = async () => {
      if (!subscriberId) return;
      try {
        const response = await axios.get(`${API}/checkins/${subscriberId}`);
        setCheckins(response.data);
        const existing = response.data.find(c => c.date === selectedDate);
        if (existing) {
          setFormData({
            on_time_percentage: existing.on_time_percentage || 50,
            off_time_percentage: existing.off_time_percentage || 50,
            dyskinesia_level: existing.dyskinesia_level || 3,
            sleep_quality: existing.sleep_quality || 5,
            energy_level: existing.energy_level || 5,
            mood: existing.mood || 5,
            notes: existing.notes || ''
          });
        }
      } catch (error) {
        console.error('Error fetching check-ins:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCheckins();
  }, [subscriberId, selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/checkins`, {
        subscriber_id: subscriberId,
        date: selectedDate,
        ...formData
      });
      toast.success('Observation saved');
      const response = await axios.get(`${API}/checkins/${subscriberId}`);
      setCheckins(response.data);
    } catch (error) {
      toast.error('Failed to save observation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <BiologicalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-bio-accent/30 border-t-bio-accent rounded-full animate-spin" />
        </div>
      </BiologicalLayout>
    );
  }

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="tracking-page">

        {/* Title */}
        <div className="mb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-bio-faint mb-3 font-['Work_Sans']"
             data-testid="tracking-label">
            Daily Observation
          </p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-light text-bio-primary leading-tight"
              data-testid="tracking-title">
            How are things today
          </h1>
        </div>

        {/* Opening */}
        <section className="mb-12" data-testid="tracking-opening">
          <p className="text-base text-bio-secondary leading-[1.8] font-['Work_Sans'] font-light max-w-2xl">
            Record what you notice. These observations help your care team understand your patterns over time.
          </p>
        </section>

        {/* Assigned tracking forms (Tracking OS V1) — only renders when there are non-default active assignments */}
        <AssignedTrackingForms />

        {/* Date */}
        <section className="mb-12" data-testid="tracking-date-section">
          <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-3 font-['Work_Sans']">
            Observing for
          </p>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-transparent border border-bio-border rounded-lg px-4 py-2.5 text-bio-primary text-sm font-['Work_Sans'] focus:outline-none focus:border-bio-accent/40"
            data-testid="date-selector"
          />
        </section>

        <form onSubmit={handleSubmit}>

          {/* ON/OFF Time */}
          <section className="mb-12" data-testid="tracking-on-off">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              Response Time
            </p>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-bio-subtle font-['Work_Sans']">ON time</span>
                  <span className="text-bio-body font-medium font-['Work_Sans']">{formData.on_time_percentage}%</span>
                </div>
                <Slider
                  value={[formData.on_time_percentage]}
                  onValueChange={([v]) => setFormData(prev => ({
                    ...prev,
                    on_time_percentage: v,
                    off_time_percentage: 100 - v
                  }))}
                  max={100}
                  step={5}
                  className="[&_[role=slider]]:bg-[#4E7A6B] [&_[role=slider]]:border-0 [&_.range]:bg-[#4E7A6B]/40"
                  data-testid="on-time-slider"
                />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-bio-subtle font-['Work_Sans']">OFF time</span>
                  <span className="text-bio-body font-medium font-['Work_Sans']">{formData.off_time_percentage}%</span>
                </div>
                <div className="h-1.5 bg-bio-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-bio-accent/60 rounded-full transition-all"
                    style={{ width: `${formData.off_time_percentage}%` }}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Dyskinesia */}
          <section className="mb-12" data-testid="tracking-dyskinesia">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-5 font-['Work_Sans']">
              Involuntary Movement
            </p>
            <div className="flex justify-between text-sm mb-3">
              <span className="text-bio-subtle font-['Work_Sans']">None</span>
              <span className="text-bio-body font-medium font-['Work_Sans']">{formData.dyskinesia_level}/10</span>
              <span className="text-bio-subtle font-['Work_Sans']">Significant</span>
            </div>
            <Slider
              value={[formData.dyskinesia_level]}
              onValueChange={([v]) => setFormData(prev => ({ ...prev, dyskinesia_level: v }))}
              max={10}
              step={1}
              className="[&_[role=slider]]:bg-[#7E6B9C] [&_[role=slider]]:border-0"
              data-testid="dyskinesia-slider"
            />
          </section>

          {/* Body Signals: Sleep, Energy, Mood */}
          <section className="mb-12" data-testid="tracking-body-signals">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-6 font-['Work_Sans']">
              Body Signals
            </p>
            <div className="space-y-8">
              {/* Sleep */}
              <div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-bio-body font-['Work_Sans']">Sleep quality</span>
                  <span className="text-bio-body font-medium font-['Work_Sans']">{formData.sleep_quality}/10</span>
                </div>
                <div className="flex justify-between text-[11px] mb-2 text-bio-ghost">
                  <span>Poor</span><span>Excellent</span>
                </div>
                <Slider
                  value={[formData.sleep_quality]}
                  onValueChange={([v]) => setFormData(prev => ({ ...prev, sleep_quality: v }))}
                  max={10}
                  step={1}
                  className="[&_[role=slider]]:bg-[#5C6B8A] [&_[role=slider]]:border-0"
                  data-testid="sleep-slider"
                />
              </div>

              {/* Energy */}
              <div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-bio-body font-['Work_Sans']">Energy level</span>
                  <span className="text-bio-body font-medium font-['Work_Sans']">{formData.energy_level}/10</span>
                </div>
                <div className="flex justify-between text-[11px] mb-2 text-bio-ghost">
                  <span>Low</span><span>High</span>
                </div>
                <Slider
                  value={[formData.energy_level]}
                  onValueChange={([v]) => setFormData(prev => ({ ...prev, energy_level: v }))}
                  max={10}
                  step={1}
                  className="[&_[role=slider]]:bg-[#4E7A6B] [&_[role=slider]]:border-0"
                  data-testid="energy-slider"
                />
              </div>

              {/* Mood */}
              <div>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-bio-body font-['Work_Sans']">Mood</span>
                  <span className="text-bio-body font-medium font-['Work_Sans']">{formData.mood}/10</span>
                </div>
                <div className="flex justify-between text-[11px] mb-2 text-bio-ghost">
                  <span>Low</span><span>Great</span>
                </div>
                <Slider
                  value={[formData.mood]}
                  onValueChange={([v]) => setFormData(prev => ({ ...prev, mood: v }))}
                  max={10}
                  step={1}
                  className="[&_[role=slider]]:bg-[#8C6B55] [&_[role=slider]]:border-0"
                  data-testid="mood-slider"
                />
              </div>
            </div>
          </section>

          {/* Notes */}
          <section className="mb-12" data-testid="tracking-notes">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bio-faint mb-4 font-['Work_Sans']">
              Anything else you noticed
            </p>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any observations, symptoms, or notes for today..."
              className="bg-bio-surface-raised/50 border-bio-border text-bio-primary placeholder:text-bio-ghost min-h-[100px] font-['Work_Sans'] text-sm focus:border-bio-accent/40"
              data-testid="notes-input"
            />
          </section>

          {/* Save */}
          <button
            type="submit"
            disabled={saving}
            className="w-full py-3.5 rounded-xl text-sm font-['Work_Sans'] font-medium transition-all duration-300 flex items-center justify-center gap-2"
            style={{ backgroundColor: '#8C6B55', color: '#FBFaf9' }}
            data-testid="save-checkin-btn"
          >
            <Save className="w-4 h-4" strokeWidth={1.5} />
            {saving ? 'Saving...' : 'Save Observation'}
          </button>
        </form>

        {/* Recent Observations */}
        {checkins.length > 0 && (
          <section className="mt-14" data-testid="tracking-history">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-bio-subtle text-sm font-['Work_Sans'] hover:text-bio-body transition-colors"
              data-testid="history-toggle"
            >
              {showHistory ? <ChevronUp className="w-4 h-4" strokeWidth={1.5} /> : <ChevronDown className="w-4 h-4" strokeWidth={1.5} />}
              Recent observations ({checkins.length})
            </button>
            {showHistory && (
              <div className="mt-6 space-y-1">
                {checkins.slice(0, 7).map((c, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedDate(c.date)}
                    className={`w-full text-left flex items-center justify-between py-3 px-3 -mx-3 rounded-lg transition-colors ${c.date === selectedDate ? 'bg-bio-accent/8' : 'hover:bg-bio-surface-raised/50'}`}
                    data-testid={`history-item-${i}`}
                  >
                    <span className="text-sm text-bio-subtle font-['Work_Sans']">{c.date}</span>
                    <div className="flex items-center gap-4 text-[11px] font-['Work_Sans']">
                      <span className="text-bio-body">ON {c.on_time_percentage}%</span>
                      <span className="text-bio-muted">Energy {c.energy_level}/10</span>
                      <span className="text-bio-muted">Mood {c.mood}/10</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        <div className="pt-8 pb-4">
          <div className="w-8 h-px bg-bio-border mx-auto" />
        </div>
      </div>
    </BiologicalLayout>
  );
}
